import { normalizedEntropy, rankPercentile, stdDev } from './aggregations';
import { linearRegression, schoolMonthIndex } from './predictive';

export const CATEGORY_KEYS = ['curriculare', 'extracurriculare', 'transferabile'];

export const EXCLUDED_STUDENT_IDS = ['e3948f24-4048-4e0b-94bb-1b2144ac3a22'];

export const normalizeActivityKey = (key) => {
  const [prefix, ...rest] = String(key || '').split(':');
  const value = rest.join(':');
  if (prefix === 'name') {
    return `name:${value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()}`;
  }
  return `${prefix}:${value.trim()}`;
};

const CATEGORY_COLUMNS = {
  curriculare: 'credite_curriculare',
  extracurriculare: 'credite_extracurriculare',
  transferabile: 'credite_transferabile',
};

export const categoryKeyOf = (categorie) => {
  if (categorie === 'extracurricular') return 'extracurriculare';
  if (categorie === 'transferabil') return 'transferabile';
  return 'curriculare';
};

export const creditsOf = (request) => request.credite_acordate || request.credite_solicitate || 0;

export const buildDataset = ({ students, requests, categories, activities, modules, creditTypes, calendar }) => {
  const excluded = new Set(EXCLUDED_STUDENT_IDS);
  const activeStudents = (students || []).filter((student) => !excluded.has(student.id));
  const activeRequests = (requests || []).filter((request) => !excluded.has(request.id_elev));

  return {
    students: activeStudents,
    requests: activeRequests,
    categories: (categories || []).filter((row) => !excluded.has(row.id_elev)),
    activities: activities || [],
    modules: modules || [],
    creditTypes: creditTypes || [],
    calendar: calendar || [],
    moduleCodes: (modules || []).map((module) => module.cod_module),
    moduleById: new Map((modules || []).map((module) => [module.id, module])),
    typeById: new Map((creditTypes || []).map((type) => [type.id, type])),
    targetSum: (modules || []).reduce((sum, module) => sum + (module.credite_tinta || 0), 0),
  };
};

export const totalRolloverCredits = (dataset, schoolYear) =>
  dataset.categories
    .filter((row) => !schoolYear || schoolYear === 'all' || row.an_scolar === schoolYear)
    .reduce((sum, row) => sum + Number(row.credite_totale || 0), 0);

const categoryTotalsFor = (dataset, studentId, schoolYear) => {
  const rows = dataset.categories.filter(
    (row) =>
      row.id_elev === studentId &&
      (!schoolYear || schoolYear === 'all' || row.an_scolar === schoolYear),
  );
  const totals = { curriculare: 0, extracurriculare: 0, transferabile: 0 };
  rows.forEach((row) => {
    CATEGORY_KEYS.forEach((key) => {
      totals[key] += Number(row[CATEGORY_COLUMNS[key]] || 0);
    });
  });
  const sum = CATEGORY_KEYS.reduce((accumulator, key) => accumulator + totals[key], 0);
  return { totals, sum, hasRows: rows.length > 0 };
};

const groupTotals = (students, byStudent, key) => {
  const groups = new Map();
  students.forEach((student) => {
    const groupKey = student[key] || 'Nespecificat';
    const list = groups.get(groupKey) || [];
    list.push(byStudent.get(student.id)?.total || 0);
    groups.set(groupKey, list);
  });
  return groups;
};

export const buildProfiles = (dataset, schoolYear) => {
  const validated = dataset.requests.filter(
    (request) =>
      request.status === 'validat' &&
      (!schoolYear || schoolYear === 'all' || request.an_scolar === schoolYear),
  );

  const byStudent = new Map();
  dataset.students.forEach((student) => {
    byStudent.set(student.id, {
      modules: Object.fromEntries(dataset.moduleCodes.map((code) => [code, 0])),
      requestCategories: { curriculare: 0, extracurriculare: 0, transferabile: 0 },
      requestTotal: 0,
      activities: [],
      names: [],
      activityIds: new Set(),
      activityKeys: new Set(),
      activityWeights: new Map(),
      dates: [],
      monthly: new Array(10).fill(0),
    });
  });

  validated.forEach((request) => {
    const entry = byStudent.get(request.id_elev);
    if (!entry) return;
    const credits = creditsOf(request);
    const code = dataset.moduleById.get(request.id_module)?.cod_module;
    if (code) entry.modules[code] += credits;
    const categoryKey = categoryKeyOf(dataset.typeById.get(request.id_tip_credit)?.categorie);
    entry.requestCategories[categoryKey] += credits;
    entry.requestTotal += credits;
    entry.activities.push(request);
    if (request.nume_activitate) entry.names.push(request.nume_activitate);
    if (request.id_activitate_predefinita) entry.activityIds.add(request.id_activitate_predefinita);
    const activityKey = request.id_activitate_predefinita
      ? normalizeActivityKey(`id:${request.id_activitate_predefinita}`)
      : request.nume_activitate
        ? normalizeActivityKey(`name:${request.nume_activitate}`)
        : null;
    if (activityKey) {
      entry.activityKeys.add(activityKey);
      entry.activityWeights.set(activityKey, (entry.activityWeights.get(activityKey) || 0) + credits);
    }
    const timestamp = new Date(request.data_validare || request.data_cerere).getTime();
    entry.dates.push(timestamp);
    const monthIndex = schoolMonthIndex(request.data_validare || request.data_cerere);
    if (monthIndex >= 0 && monthIndex < 10) entry.monthly[monthIndex] += credits;
  });

  const totals = [];
  const resolvedByStudent = new Map();
  dataset.students.forEach((student) => {
    const entry = byStudent.get(student.id);
    const categoryTotals = categoryTotalsFor(dataset, student.id, schoolYear);
    const creditsByCategory = categoryTotals.hasRows ? categoryTotals.totals : entry.requestCategories;
    const totalCredits = categoryTotals.hasRows ? categoryTotals.sum : entry.requestTotal;
    resolvedByStudent.set(student.id, {
      creditsByCategory,
      totalCredits,
      total: totalCredits,
      rolloverCredits: Math.max(0, totalCredits - entry.requestTotal),
    });
    totals.push(totalCredits);
  });

  const totalsByClass = groupTotals(dataset.students, resolvedByStudent, 'clasa');
  const totalsBySpecialization = groupTotals(dataset.students, resolvedByStudent, 'specializare');

  const now = new Date();
  const currentMonthIndex = Math.min(9, Math.max(0, schoolMonthIndex(now.toISOString())));

  const profiles = new Map();
  dataset.students.forEach((student) => {
    const entry = byStudent.get(student.id);
    const resolved = resolvedByStudent.get(student.id);
    entry.monthly[0] += resolved.rolloverCredits;

    let running = 0;
    const cumulative = entry.monthly.map((value) => {
      running += value;
      return running;
    });
    const { slope } = linearRegression(cumulative.slice(0, currentMonthIndex + 1));

    const sortedDates = [...entry.dates].sort((a, b) => a - b);
    const gaps = sortedDates.slice(1).map((timestamp, index) => (timestamp - sortedDates[index]) / 86400000);
    const daysSinceLastActivity = sortedDates.length
      ? Math.floor((Date.now() - sortedDates[sortedDates.length - 1]) / 86400000)
      : null;

    profiles.set(student.id, {
      studentId: student.id,
      nume: student.nume,
      prenume: student.prenume,
      clasa: student.clasa,
      specializare: student.specializare,
      schoolYear: schoolYear || 'all',
      creditsByModule: entry.modules,
      creditsByCategory: resolved.creditsByCategory,
      totalCredits: resolved.totalCredits,
      activityCredits: entry.requestTotal,
      rolloverCredits: resolved.rolloverCredits,
      activityCount: entry.activities.length,
      activityIds: [...entry.activityIds],
      activityKeys: [...entry.activityKeys],
      activityVector: [...entry.activityKeys].map((key) => entry.activityWeights.get(key) || 0),
      activityKeyIndex: new Map([...entry.activityKeys].map((key, index) => [key, index])),
      activityNames: entry.names,
      moduleVector: dataset.moduleCodes.map((code) => (resolved.totalCredits > 0 ? entry.modules[code] / resolved.totalCredits : 0)),
      categoryVector: CATEGORY_KEYS.map((key) => (resolved.totalCredits > 0 ? resolved.creditsByCategory[key] / resolved.totalCredits : 0)),
      consistency: gaps.length ? stdDev(gaps) : null,
      diversityScore: normalizedEntropy(dataset.moduleCodes.map((code) => entry.modules[code])),
      growthSlope: entry.activities.length > 1 ? slope : 0,
      daysSinceLastActivity,
      monthlyCumulative: cumulative,
      percentileInClass: rankPercentile(resolved.totalCredits, totalsByClass.get(student.clasa || 'Nespecificat') || []),
      percentileInSpecialization: rankPercentile(
        resolved.totalCredits,
        totalsBySpecialization.get(student.specializare || 'Nespecificat') || [],
      ),
      percentileInSchool: rankPercentile(resolved.totalCredits, totals),
    });
  });

  return profiles;
};

export const buildProfile = (dataset, studentId, schoolYear) => buildProfiles(dataset, schoolYear).get(studentId) || null;

export const buildFeatureVectors = (profiles) =>
  [...profiles.values()].map((profile) => ({
    id: profile.studentId,
    vector: [...Object.values(profile.creditsByModule), profile.totalCredits],
  }));
